import { formatDistanceStrict, formatISO, isValid, type Locale } from 'date-fns';
import type { Language } from '@/stores/useClientSideSettingsStore';
import { cs, de, enUS, fr, pl } from 'date-fns/locale';
import { i18n } from '@/plugins/i18n';

export const getNowInSeconds = () => Math.floor(Date.now() / 1000);

export const getNowISO = () => formatISO(Date.now());

export const isValidDate = (value: unknown): value is Date => isValid(value);

interface Config {
  inMinutes: boolean;
  showDays: boolean;
  fullTime: boolean;
}

const defaultConfig: Config = {
  inMinutes: false,
  showDays: false,
  fullTime: false,
};

const localesMap: Record<Language, Locale | undefined> = {
  de: de,
  en: enUS,
  fr: fr,
  cs: cs,
  pl: pl,
};

export const getDateInSeconds = (date: string | null) => {
  if (!date) return null;
  return new Date(date).getTime() / 1_000;
};

export const splitSeconds = (totalSeconds: number): { hours: number; minutes: number; seconds: number } => {
  const HOUR = 3600;

  const hours = Math.floor(totalSeconds / HOUR);
  const minutes = Math.floor((totalSeconds % HOUR) / 60);
  const seconds = totalSeconds % 60;

  return { hours, minutes, seconds };
};

export const formatDurationAsMinutesAndSeconds = (totalSeconds: number) => {
  const { hours, minutes, seconds } = splitSeconds(totalSeconds);
  const hoursAndMinutes = hours * 60 + minutes;
  return formatTimeParts([hoursAndMinutes, seconds]);
};

export const formatDurationAsTime = (totalSeconds: number) => {
  const { hours, minutes, seconds } = splitSeconds(totalSeconds);
  return formatTimeParts([hours, minutes, seconds]);
};

export const formatDuration = (startEpoch: number, endEpoch: number, config: Partial<Config> = {}): string => {
  const _config = { ...defaultConfig, ...config };

  let totalSeconds = endEpoch - startEpoch;
  // there are some edge cases where start epoch is few seconds ahead of end epoch
  totalSeconds = totalSeconds < 0 ? 0 : totalSeconds;

  const { hours, minutes, seconds } = splitSeconds(totalSeconds);

  // if more than 24 hours and showDays is true, e.g., '5 days'
  if (hours > 24 && _config.showDays) {
    const startDate = new Date(startEpoch * 1_000);
    const endDate = new Date(endEpoch * 1_000);

    const { locale } = i18n.global; // get current language
    const currentLocale: Locale = localesMap[locale.value as Language] || enUS;
    return formatDistanceStrict(endDate, startDate, { locale: currentLocale });
  }

  // if fullTime is true, show hours, minutes, seconds, e.g., '02:15:45'
  if (_config.fullTime) {
    return formatTimeParts([hours, minutes, seconds]);
  }

  //if inMinutes is true, show total minutes and seconds, e.g., '90:45'
  if (_config.inMinutes) {
    return formatTimeParts([hours * 60 + minutes, seconds]);
  }

  // if more than one hour, show hours, minutes, seconds, e.g., '01:30:30'
  if (hours > 0 && !_config.inMinutes && !_config.showDays) {
    return formatTimeParts([hours, minutes, seconds]);
  }

  // otherwise, show minutes and seconds, e.g., '04:20'
  return formatTimeParts([hours * 60 + minutes, seconds]);
};

// turns [hours, minutes, seconds] into '02:15:45' format
const formatTimeParts = (timeParts: [number, number] | [number, number, number]) => {
  return timeParts.map((part) => String(part).padStart(2, '0')).join(':');
};
